import React from 'react';
import { Select } from 'antd';
import { RobotOutlined } from '@ant-design/icons';

import { useXoeExperts } from '../../hooks/xoe/useXoe';
import { getExpertIconComponent } from '../../utils/xoe/xoeExpertIcons';
import type { XoeExpert } from '../../types/xoe/XoeTypes';
import type { XoeExpertSelectorProps } from '../../types/xoe/XoeUiTypes';

import './xoe.css';

// Internal sentinel value — never sent to the backend.
const AUTO_VALUE = '__auto__';

const XoeExpertSelector: React.FC<XoeExpertSelectorProps> = ({
	expertId,
	onChange,
	disabled,
}) => {
	const { data: experts = [] } = useXoeExperts();

	return (
		<Select
			value={expertId ?? AUTO_VALUE}
			size="small"
			variant="borderless"
			disabled={disabled}
			onChange={(val: string) => onChange(val === AUTO_VALUE ? null : val)}
			popupMatchSelectWidth={false}
			optionLabelProp="label"
			data-testid="xoe-expert-selector"
		>
			{/* Auto option — defers expert selection to the S8 router per-turn */}
			<Select.Option value={AUTO_VALUE} label="Auto">
				<span className="mg-xoe-expert-option">
					<RobotOutlined />
					<span>Auto</span>
				</span>
			</Select.Option>

			{/* One option per clinic expert */}
			{experts.map((expert: XoeExpert) => {
				// The expert icon is a kebab-case antd icon NAME — resolve it to a component
				// (robot fallback for unknown/empty names) so it never renders as raw text.
				const ExpertIcon = getExpertIconComponent(expert.icon);
				return (
					<Select.Option key={expert.id} value={String(expert.id)} label={expert.name}>
						<span className="mg-xoe-expert-option">
							<ExpertIcon className="mg-xoe-expert-icon" />
							<span>{expert.name}</span>
							{expert.specialty && (
								<span className="mg-xoe-expert-specialty">
									{expert.specialty}
								</span>
							)}
							{expert.is_default && (
								<span className="mg-xoe-expert-default" title="Default expert">
									★
								</span>
							)}
						</span>
					</Select.Option>
				);
			})}
		</Select>
	);
};

export default XoeExpertSelector;
